
import { createApiCall } from 'services'
import { ActionType, type CustomerDataResponse } from 'store/customers/types'
import type {
  CreateCustomerResponse,
  CreateLeanCustomerPayload,
  UpdateCustomerPayload,
  UpdateCustomerResponse,
  GetCustomerByIdPayload,
  GetCustomerByIdResponse,
  GetCustomersPayload,
  GetCustomersResponse,
} from 'store/customers/types'
import { ActionCallback } from 'store/model'
import { getErrorMessage } from 'utils'
import { getCustomerById } from 'store/customers/actions'
import type { RootState } from 'store/rootReducer'

import { call, fork, put, takeLatest, select } from 'redux-saga/effects'



export function* getCustomersSaga(): Generator {
  yield takeLatest(ActionType.GET_CUSTOMERS, function* (action: ActionCallback<GetCustomersPayload>) {
    const { payload, callback } = action

    try {
      const response: GetCustomersResponse = yield call(createApiCall, {
        method: 'GET',
        url: `/customers/admin`,
        params: payload,
      })

      const { status, data } = response

      if (status === 200) {
        yield put({
          type: ActionType.SET_CUSTOMERS,
          payload: data,
        })
      }

      if (callback) callback()
    } catch (error) {
      console.error(error)
    }
  })
}

export function* getCustomerByIdSaga(): Generator {
  yield takeLatest(ActionType.GET_CUSTOMER, function* (action: ActionCallback<GetCustomerByIdPayload>) {
    const { payload, callback } = action
    const { customerId, ...rest } = payload

    try {
      const response: GetCustomerByIdResponse = yield call(createApiCall, {
        method: 'GET',
        url: `/customers/admin/${customerId}`,
        params: rest,
      })

      const { status, data, statusText } = response

      let success = false

      if (status === 200) {
        yield put({
          type: ActionType.SET_CUSTOMER,
          payload: data,
        })
        success = true
      } else {
        success = false
      }

      if (!callback) return

      if (success) {
        callback({ success, content: data })
      } else {
        callback({ success, content: statusText })
      }
    } catch (error) {
      console.error(error)
      if (callback) callback({ success: false, content: getErrorMessage(error) })
    }
  })
}

export function* createCustomerSaga(): Generator {
  yield takeLatest(ActionType.CREATE_CUSTOMER, function* (action: ActionCallback<CreateLeanCustomerPayload>) {
    const { payload, callback } = action

    try {
      const response: CreateCustomerResponse = yield call(createApiCall, {
        method: 'POST',
        url: `/customers/admin/create`,
        data: payload,
      })

      const { status, data, statusText } = response

      let success = false

      if (status === 201) {
        yield put({
          type: ActionType.SET_CUSTOMER,
          payload: data,
        })

        success = true
      } else {
        success = false
      }

      if (!callback) return

      if (success) {
        callback({ success, content: data })
      } else {
        callback({ success, content: statusText })
      }
    } catch (error) {
      console.error(error)
      if (callback) callback({ success: false, content: getErrorMessage(error) })
    }
  })
}

export function* updateCustomerSaga(): Generator {
  yield takeLatest(ActionType.UPDATE_CUSTOMER, function* (action: ActionCallback<UpdateCustomerPayload>) {
    const { payload, callback } = action
    const { id, ...updateData } = payload

    try {
      const response: UpdateCustomerResponse = yield call(createApiCall, {
        method: 'PUT',
        url: `/customers/admin/${id}`,
        data: updateData,
      })

      const { status, data, statusText } = response

      let success = false

      if (status === 200) {
        const customerState: CustomerDataResponse | null = yield select(
          (state: RootState) => state.customers.customer
        )

        const limit = customerState?.certificates?.limit ?? 10
        const offset = customerState?.certificates?.offset ?? 0

        yield put(
          getCustomerById(
            {
              customerId: id,
              limit,
              offset,
            },
            () => {}
          )
        )
        success = true
      } else {
        success = false
      }

      if (!callback) {
        return
      }

      if (success) {
        callback({ success, content: data })
      } else {
        callback({ success, content: statusText })
      }
    } catch (error: any) {
      console.error(error)
      if (callback) {
        let errorMessage = 'Failed to update owner'
        
        // Handle specific error cases based on status codes
        if (error.response) {
          const { status, data } = error.response
          switch (status) {
            case 400:
              errorMessage = data?.message || 'Invalid data provided. Please check your input.'
              break
            case 404:
              errorMessage = 'Owner not found. They may have been deleted.'
              break
            case 409:
              errorMessage = 'Email address is already in use by another owner.'
              break
            case 500:
              errorMessage = 'Server error. Please try again later.'
              break
            default:
              errorMessage = data?.message || 'Failed to update owner'
          }
        }
        
        callback({ success: false, content: errorMessage })
      }
    }
  })
}
export default [fork(getCustomersSaga), fork(getCustomerByIdSaga), fork(createCustomerSaga), fork(updateCustomerSaga)]
